import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { cancelPreVenda, getPreVenda, getPreVendas } from '../services/api';
import { getAuthPermissoes } from '../services/authStorage';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';

const STATUS_LABEL = {
  aberta: 'Aberta',
  em_caixa: 'No caixa',
  finalizada: 'Finalizada',
  cancelada: 'Cancelada',
};

const STATUS_COLOR = {
  aberta: 'success',
  em_caixa: 'warning',
  finalizada: 'default',
  cancelada: 'error',
};

const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
};

const emptyFilters = () => ({
  de: '',
  ate: '',
  status: 'aberta',
  tipo: '',
  termo: '',
});

export default function PreVendaList() {
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const permissions = useMemo(() => getAuthPermissoes(), []);
  const can = useCallback(
    (key) => Boolean(permissions?.[key]),
    [permissions]
  );

  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], totalPages: 1, totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    if (!can('preVenda')) {
      setError('Sem permissão de pré-venda.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getPreVendas({
        ...applied,
        estabelecimentoId: estabelecimentoId || undefined,
        indice: page,
        qtde: 20,
      });
      setData(response.data || { items: [], totalPages: 1, totalItems: 0 });
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível carregar as pré-vendas.');
    } finally {
      setLoading(false);
    }
  }, [applied, page, can, estabelecimentoId]);

  useEffect(() => {
    load();
  }, [load]);

  const preVendaAccessors = useMemo(() => ({
    codigo: (r) => r.codigoBarras,
    abertura: (r) => r.dataAbertura,
    operador: (r) => r.usuarioAbertura,
    total: (r) => Number(r.valorTotal) || 0,
  }), []);
  const { sorted: preVendasOrdenadas, orderBy, order, requestSort } = useClientSort(
    data.items || [],
    'codigoBarras',
    'asc',
    preVendaAccessors,
  );

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setError('');
    try {
      const response = await getPreVenda(id);
      setDetail(response.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Erro ao carregar a pré-venda.');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!detail?.id) return;
    if (!window.confirm(`Cancelar a pré-venda ${detail.codigoBarras}?`)) return;
    setCancelling(true);
    try {
      await cancelPreVenda(detail.id, {
        motivo: 'Cancelada pelo Gestão',
      });
      setDetailOpen(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível cancelar.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Pré-vendas"
        subtitle="Cupons impressos e cartões/comandas abertos, no caixa ou finalizados."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            label="De"
            type="date"
            size="small"
            value={filters.de}
            onChange={(e) => setFilters((p) => ({ ...p, de: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={filters.ate}
            onChange={(e) => setFilters((p) => ({ ...p, ate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Status"
            size="small"
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="aberta">Abertas</MenuItem>
            <MenuItem value="em_caixa">No caixa</MenuItem>
            <MenuItem value="finalizada">Finalizadas</MenuItem>
            <MenuItem value="cancelada">Canceladas</MenuItem>
          </TextField>
          <TextField
            select
            label="Tipo"
            size="small"
            value={filters.tipo}
            onChange={(e) => setFilters((p) => ({ ...p, tipo: e.target.value }))}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="impressa">Impressa</MenuItem>
            <MenuItem value="cartao">Cartão</MenuItem>
          </TextField>
          <TextField
            label="Busca"
            size="small"
            placeholder="PV000123, cartão, CPF..."
            value={filters.termo}
            onChange={(e) => setFilters((p) => ({ ...p, termo: e.target.value }))}
            sx={{ minWidth: 220 }}
          />
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => {
              setPage(1);
              setApplied({ ...filters });
            }}
          >
            Filtrar
          </Button>
        </Stack>
      </Paper>

      {loading && <LinearProgress />}
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="codigo" label="Código" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cliente" label="Cliente" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="abertura" label="Abertura" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="operador" label="Operador" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="total" label="Total" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {preVendasOrdenadas.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell data-label="Código">
                  <Typography fontWeight={800}>{row.codigoBarras}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.tipo === 'cartao'
                      ? `Cartão ${row.codigoCartao || '—'}`
                      : 'Impressa'}
                  </Typography>
                </TableCell>
                <TableCell data-label="Cliente">{row.cliente}</TableCell>
                <TableCell data-label="Abertura">{fmtDate(row.dataAbertura)}</TableCell>
                <TableCell data-label="Operador">{row.usuarioAbertura}</TableCell>
                <TableCell data-label="Total" align="right">
                  {money(row.valorTotal)}
                </TableCell>
                <TableCell data-label="Status">
                  <Chip
                    size="small"
                    label={STATUS_LABEL[row.status] || row.status}
                    color={STATUS_COLOR[row.status] || 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openDetail(row.id)}>
                    Detalhe
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && (data.items || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                    Nenhuma pré-venda encontrada.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {(data.totalPages || 1) > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            page={page}
            count={data.totalPages || 1}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Stack>
      )}

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Pré-venda {detail?.codigoBarras || ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && <LinearProgress sx={{ mb: 2 }} />}
          {detail && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={STATUS_LABEL[detail.status] || detail.status}
                  color={STATUS_COLOR[detail.status] || 'default'}
                />
                <Chip label={detail.tipo === 'cartao' ? 'Cartão' : 'Impressa'} />
                {detail.codigoCartao && (
                  <Chip variant="outlined" label={`Cartão ${detail.codigoCartao}`} />
                )}
              </Stack>
              <Typography variant="body2">
                Cliente: <strong>{detail.cliente}</strong>
                {detail.documento ? ` · Doc: ${detail.documento}` : ''}
              </Typography>
              <Typography variant="body2">
                Aberta em {fmtDate(detail.dataAbertura)} por {detail.usuarioAbertura}
                {detail.pdvAbertura ? ` (${detail.pdvAbertura})` : ''}
              </Typography>
              {detail.dataFechamento && (
                <Typography variant="body2">
                  Fechada em {fmtDate(detail.dataFechamento)}
                  {detail.usuarioFechamento
                    ? ` por ${detail.usuarioFechamento}`
                    : ''}
                  {detail.vendaId ? ` · Venda #${detail.vendaId}` : ''}
                </Typography>
              )}
              <Typography variant="h6">{money(detail.valorTotal)}</Typography>

              <ResponsiveTable>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Qtd</TableCell>
                      <TableCell align="right">Unit.</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Operador</TableCell>
                      <TableCell>Quando</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.itens || []).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {item.produtoDescricao}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.produtoCodigo}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{item.quantidade}</TableCell>
                        <TableCell align="right">{money(item.valorUnitario)}</TableCell>
                        <TableCell align="right">{money(item.valorTotal)}</TableCell>
                        <TableCell>{item.usuario}</TableCell>
                        <TableCell>{fmtDate(item.dataLancamento)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTable>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detail &&
            (detail.status === 'aberta' || detail.status === 'em_caixa') &&
            can('preVenda') && (
              <Button color="error" disabled={cancelling} onClick={handleCancel}>
                Cancelar pré-venda
              </Button>
            )}
          <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
